import {Button, Chip, MenuItem, Paper} from '@material-ui/core';
import {Loaded} from 'Core/models/Async';
import {unwrapValidatableWithDefault} from 'Core/models/Valid';
import * as EnrollTrackUserActions from 'Enrollment/ducks/enrollTrackUser';
import * as E from 'fp-ts/lib/Either';
import {constTrue, not} from 'fp-ts/lib/function';
import * as NEA from 'fp-ts/lib/NonEmptyArray';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {Lens} from 'monocle-ts';
import * as React from 'react';
import {RouteComponentProps, withRouter} from 'react-router-dom';
import OutlinedSelect from 'Shared/components/OutlinedSelect';
import RequiredTextInput from 'Shared/components/RequiredTextInput';
import ValidatableEmailInput from 'Shared/components/ValidatableEmailInput';
import ValidatablePhoneInput from 'Shared/components/ValidatablePhoneInput';
import ValidatableTextInput from 'Shared/components/ValidatableTextInput';
import {RoleBase} from 'Users/api';
import 'Users/components/UserAdministration/EnrollTrackUserForm.sass';
import {
  EnrollTrackUser,
  eqValidatableEnrollTrackUser,
  InvalidEnrollTrackUser,
  ValidEnrollTrackUser,
} from 'Users/models/EnrollTrackUser';
import {UserRouteConstants} from 'Users/Routes';
import {EnrollTrackRole} from 'Users/models/User/EnrollmentTrackingUserType';

type EnrollTrackUserFormProps = {
  user: EnrollTrackUser;
  original: O.Option<EnrollTrackUser>;
  enrollTrackUserId: O.Option<string>;
  roles: RoleBase[];
} & RouteComponentProps &
  typeof EnrollTrackUserActions;

const enrollTrackUserLens = Lens.fromProp<InvalidEnrollTrackUser | ValidEnrollTrackUser>();

export const EnrollTrackUserForm = (props: EnrollTrackUserFormProps) => {
  const {user, original, roles} = props;
  const unwrappedUser = unwrapValidatableWithDefault<InvalidEnrollTrackUser | ValidEnrollTrackUser>()(user);
  const equalsCurrentEnrollTrack = (otherEnrollTrackUser: EnrollTrackUser) =>
    eqValidatableEnrollTrackUser.equals(user, otherEnrollTrackUser);
  const hasEnrollTrackChangedOrIsBeingAdded = pipe(original, O.fold(constTrue, not(equalsCurrentEnrollTrack)));
  const allowSave = E.isRight(user) && hasEnrollTrackChangedOrIsBeingAdded;
  const formLabel = pipe(
    props.enrollTrackUserId,
    O.fold(
      () => 'Add',
      () => 'Edit'
    )
  );
  const changeUser = (enrollTrackUser: InvalidEnrollTrackUser | ValidEnrollTrackUser) => {
    props.changeUser(
      new Loaded(
        ValidEnrollTrackUser.create(
          enrollTrackUser.firstName,
          enrollTrackUser.lastName,
          enrollTrackUser.username,
          enrollTrackUser.phoneNumber,
          enrollTrackUser.email,
          pipe(
            () => enrollTrackUser.roles.indexOf(EnrollTrackRole.UserSupport.toString()) > 0,
            () => false
          ),
          enrollTrackUser.roles
        )
      )
    );
  };

  const getRoleName = (roleId: string) =>
    roles.find(role => role?.id?.toString() === roleId.toString() && role?.id !== EnrollTrackRole.UserSupport)?.name;

  return (
    <div className="enroll-track-user-form">
      <Paper className="enroll-track-user-form__paper">
        <h2 className="agent-entry__title">
          {formLabel}
          &nbsp;EnrollTrack User
        </h2>
        <p className="agent-entry__description">Please enter the enrollment file tracking user's information below.</p>
        <form className="agent-entry__form">
          <RequiredTextInput
            label="First Name"
            className="agent-entry__field"
            onChange={firstName => changeUser(enrollTrackUserLens('firstName').set(firstName)(unwrappedUser))}
            value={unwrappedUser.firstName}
          />
          <RequiredTextInput
            label="Last Name"
            className="agent-entry__field"
            onChange={lastName => changeUser(enrollTrackUserLens('lastName').set(lastName)(unwrappedUser))}
            value={unwrappedUser.lastName}
          />
          <ValidatableTextInput
            label="User Name"
            className="agent-entry__field"
            onValidate={props.validateUsername}
            value={unwrappedUser.username}
          />
          <OutlinedSelect
            className="enroll-track-user-form__permissions"
            label="Roles"
            multiple
            value={unwrappedUser.roles}
            onChange={e =>
              changeUser(enrollTrackUserLens('roles').set((e.target.value as unknown) as string[])(unwrappedUser))
            }
            renderValue={(selected: any) => (
              <div className="enroll-track-user-form__chips">
                {selected.map((value: string) => (
                  <Chip key={value} label={getRoleName(value)} className="enroll-track-user-form__chip" />
                ))}
              </div>
            )}
          >
            {pipe(
              roles,
              NEA.fromArray,
              O.map(NEA.map(role => role)),
              O.fold(
                () => '' as React.ReactNode,
                NEA.map(roleName => (
                  <MenuItem key={roleName?.id} value={roleName?.id}>
                    {roleName?.name}
                  </MenuItem>
                ))
              )
            )}
          </OutlinedSelect>
          <ValidatableEmailInput
            className="agent-entry__field"
            onValidate={props.validateEmail}
            label="Email"
            value={unwrappedUser.email}
          />
          <ValidatablePhoneInput
            className="agent-entry__field"
            value={unwrappedUser.phoneNumber}
            label="Phone"
            onValidate={props.validatePhone}
          />
          <div className="agent-entry-form__buttons">
            <div className="agent-entry-form__return-button">
              <Button
                variant="contained"
                onClick={() => {
                  props.history.push(UserRouteConstants.USERS);
                }}
              >
                Cancel
              </Button>
            </div>
            <Button
              className="agent-entry-form__add-button"
              color="primary"
              type="submit"
              variant="contained"
              disabled={!allowSave}
              onClick={e => {
                pipe(
                  O.some((user: ValidEnrollTrackUser) => (userId: O.Option<string>) => props.save(user, userId)),
                  O.ap(O.fromEither(user)),
                  O.ap(O.some(props.enrollTrackUserId))
                );
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              Save
            </Button>
          </div>
        </form>
      </Paper>
    </div>
  );
};

export default withRouter(EnrollTrackUserForm);
